/**
 * Mapbox `MapView` adapter — wraps the existing `mapboxgl.Map` boot sequence
 * used by both LocationPickers. Same style URLs (streets-v12 +
 * satellite-streets-v12), same default zoom / center, same imperative API
 * surface (flyTo / fitBounds / project). Markers and overlays are placed by
 * the consumer via the `Marker` component (which uses a React context to
 * find this view's `mapboxgl.Map` instance).
 *
 * Pin-drop is uniformly **left-click** here — matches the architect's locked
 * decision in #321. The pre-existing right-click (`contextmenu`) trigger in
 * the historical LocationPickers is gone now that this adapter owns the
 * trigger.
 *
 * POI-click behavior (snap to a labeled POI under the cursor and surface its
 * name) is wired on the Google path via `MapMouseEvent.placeId` +
 * `getPlaceDetails`. Mapbox doesn't surface a place id on click, so a POI click
 * here just drops a plain pin at the clicked coordinates (then reverse-geocodes
 * like any other map click). Mapbox is the dormant fallback provider, so this
 * graceful degradation is acceptable; reach for `queryRenderedFeatures` on the
 * POI symbol layers if full parity is ever needed.
 */

import { useEffect, useRef, useState, useImperativeHandle, createContext, useContext } from 'react';
import type * as React from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { MapViewProps, MapHandle, MapMouseEvent } from '../../types';
import { getMapboxToken } from './token';

// Set the access token at module load time. Safe to overwrite — mapboxgl
// reads it lazily per Map.
const cachedToken = getMapboxToken();
if (cachedToken) {
  mapboxgl.accessToken = cachedToken;
}

/**
 * Per-MapView context shared with descendant `Marker` components so they can
 * attach to the same `mapboxgl.Map` instance without prop-drilling.
 */
interface MapboxMapContextValue {
  map: mapboxgl.Map | null;
  ready: boolean;
}
const MapboxMapContext = createContext<MapboxMapContextValue>({ map: null, ready: false });

export function useMapboxMap(): MapboxMapContextValue {
  return useContext(MapboxMapContext);
}

const STYLE_URLS: Record<NonNullable<MapViewProps['style']>, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};

export const MapboxMapView: React.FC<MapViewProps> = ({
  center,
  zoom,
  style = 'streets',
  interactive = true,
  onMapClick,
  onMoveOrZoom,
  onLoad,
  mapRef,
  children,
  style_,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<mapboxgl.Map | null>(null);
  const [ready, setReady] = useState(false);
  const onMapClickRef = useRef(onMapClick);
  const onMoveOrZoomRef = useRef(onMoveOrZoom);
  const onLoadRef = useRef(onLoad);

  // Refs keep handler identities stable so we don't tear down + recreate the
  // map every render — same pattern the historical LocationPickers used.
  useEffect(() => { onMapClickRef.current = onMapClick; }, [onMapClick]);
  useEffect(() => { onMoveOrZoomRef.current = onMoveOrZoom; }, [onMoveOrZoom]);
  useEffect(() => { onLoadRef.current = onLoad; }, [onLoad]);

  useImperativeHandle(
    mapRef,
    (): MapHandle => ({
      projectLatLng: (ll) => {
        const m = mapInstanceRef.current;
        if (!m) return null;
        const pt = m.project([ll.lng, ll.lat]);
        return { x: pt.x, y: pt.y };
      },
      flyTo: (ll, z) => {
        const m = mapInstanceRef.current;
        if (!m) return;
        m.flyTo({ center: [ll.lng, ll.lat], zoom: z ?? Math.max(m.getZoom(), 19) });
      },
      fitBounds: (points, opts) => {
        const m = mapInstanceRef.current;
        if (!m || points.length === 0) return;
        if (points.length === 1) {
          m.flyTo({ center: [points[0].lng, points[0].lat], zoom: opts?.maxZoom ?? 19 });
          return;
        }
        const first: [number, number] = [points[0].lng, points[0].lat];
        const bounds = new mapboxgl.LngLatBounds(first, first);
        points.forEach((p) => bounds.extend([p.lng, p.lat]));
        m.fitBounds(bounds, {
          padding: opts?.padding ?? 60,
          maxZoom: opts?.maxZoom ?? 19,
          duration: opts?.duration ?? 600,
        });
      },
    }),
    [],
  );

  // Init exactly once. Style changes route through map.setStyle below so the
  // map instance survives provider-driven style toggles (streets <-> satellite).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (mapInstanceRef.current) return;
    if (!containerRef.current) return;

    const token = getMapboxToken();
    if (!token) {
      // Log once — caller will see a blank canvas. Same fail-soft behavior the
      // historical LocationPickers had.
      console.error('[maps:mapbox] Mapbox token not found');
      return;
    }
    if (!mapboxgl.accessToken) mapboxgl.accessToken = token;

    try {
      const map = new mapboxgl.Map({
        container: containerRef.current,
        style: STYLE_URLS[style],
        center: [center.lng, center.lat],
        zoom,
        interactive,
      });
      mapInstanceRef.current = map;

      map.on('load', () => {
        setReady(true);
        onLoadRef.current?.();
      });
      map.on('move', () => onMoveOrZoomRef.current?.());
      map.on('zoom', () => onMoveOrZoomRef.current?.());
      map.on('resize', () => onMoveOrZoomRef.current?.());

      // Left-click pin drop — uniform trigger across providers (replaces the
      // historical contextmenu/right-click trigger).
      map.on('click', (e) => {
        const evt: MapMouseEvent = {
          lngLat: { lat: e.lngLat.lat, lng: e.lngLat.lng },
          point: { x: e.point.x, y: e.point.y },
          originalEvent: e.originalEvent,
        };
        onMapClickRef.current?.(evt);
      });
    } catch (err) {
      console.error('[maps:mapbox] Error initializing map:', err);
    }

    return () => {
      mapInstanceRef.current?.remove();
      mapInstanceRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Style toggle without remounting the map. Centre / zoom are intentionally
  // *not* in this effect — re-applying them on every prop change would fight
  // the user's pan/zoom. Use `flyTo` via `mapRef` to imperatively recenter.
  useEffect(() => {
    const m = mapInstanceRef.current;
    if (!m || !ready) return;
    m.setStyle(STYLE_URLS[style]);
  }, [style, ready]);

  return (
    <MapboxMapContext.Provider value={{ map: mapInstanceRef.current, ready }}>
      <div ref={containerRef} className={className} style={style_} />
      {children}
    </MapboxMapContext.Provider>
  );
};
